import React, { useState } from 'react';
import NavBar from './NavBar';
import { 
  Moon, Menu, X, Shield, FileCheck, UserCheck, AlertCircle, 
  Award, HeartHandshake, CheckCircle2, Info, Gift
} from 'lucide-react';

const LegalPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const securityMeasures = [
    {
      title: "Vérification des Qualifications",
      content: "Toutes les professionnelles doivent fournir leurs diplômes et certifications qui sont minutieusement vérifiés.",
      icon: FileCheck
    },
    {
      title: "Entretien Personnel",
      content: "Chaque professionnelle passe un entretien approfondi pour évaluer ses compétences et son expérience.",
      icon: UserCheck
    },
    {
      title: "Réseau de Confiance",
      content: "Nos professionnelles sont recommandées par leurs pairs et font partie d'un réseau établi de personnel médical.",
      icon: HeartHandshake
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
     <NavBar />

      {/* Hero Section */}
      <div className="relative bg-sky-100 overflow-hidden">
        <div className="absolute inset-0">
          <div className="cloud cloud-1" />
          <div className="cloud cloud-2" />
          <div className="cloud cloud-3" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 py-12 md:py-20">
          <h1 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 md:mb-6">
            Sécurité et Protection
          </h1>
          <p className="text-lg md:text-xl text-slate-600">
            Nos engagements pour votre tranquillité d'esprit
          </p>
        </div>
      </div>

      {/* Contenu Principal */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Section Sécurité */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-slate-800 mb-8">Nos Mesures de Sécurité</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {securityMeasures.map((measure, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md p-6 transform transition hover:shadow-lg hover:scale-105"
              >
                <measure.icon className="w-12 h-12 text-amber-400 mb-4" />
                <h3 className="text-xl font-semibold text-slate-800 mb-3">{measure.title}</h3>
                <p className="text-slate-600">{measure.content}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Section Important */}
        <div className="bg-amber-50 rounded-lg p-6 mb-12 border-l-4 border-amber-400">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-amber-500 mr-4 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Information Importante</h3>
              <p className="text-slate-600 mb-4">
                Nurse de Nuit est une plateforme de mise en relation et non une société. 
                Nous facilitons la connexion entre les professionnelles de santé qualifiées et les familles.
              </p>
            </div>
          </div>
        </div>

        {/* Section Œuvre Caritative */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Œuvre Caritative</h2>
          <div className="flex items-start space-x-4">
            <Gift className="w-10 h-10 text-amber-400 flex-shrink-0" />
            <div className="space-y-4">
              <p className="text-slate-600">
                Chez Nurse de Nuit, notre engagement va au-delà du sommeil de votre bébé. Nous soutenons activement des associations dédiées à l’aide à l’enfance et au bien-être des enfants en difficulté. En choisissant nos services de garde de nuit, vous contribuez à une démarche solidaire et responsable, fondée sur des valeurs humaines fortes.
              </p>
              <p className="text-slate-600">
                Vous pouvez également nous faire don de produits de puériculture que vous n'utilisez plus. Ces produits seront redistribués à des familles en difficulté ou à des organisations humanitaires.
              </p>
              <div className="bg-sky-50 p-4 rounded-lg">
                <p className="text-slate-600 text-sm">
                  Pour plus d'informations sur nos actions caritatives ou pour organiser un don de matériel, n'hésitez pas à nous contacter directement.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section Responsabilité */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Responsabilité</h2>
          <div className="space-y-4">
            <p className="text-slate-600">
              Le service proposé par Nurse de Nuit se limite à la mise en relation entre les professionnelles 
              de santé et les familles. Les contrats de prestation sont établis directement entre les deux parties, 
              sans l'intervention de Nurse de Nuit.
            </p>
            <div className="flex items-start space-x-4 bg-slate-50 p-4 rounded-lg">
              <Info className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
              <p className="text-slate-600">
                Les parents sont invités à effectuer leurs propres vérifications et à s'assurer de la 
                compatibilité de la professionnelle avec leurs besoins spécifiques.
              </p>
            </div>
          </div>
        </div>

        {/* Section Vérifications */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Processus de Vérification</h2>
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              <p className="text-slate-600">Vérification approfondie des diplômes et certifications</p>
            </div>
            <div className="flex items-center space-x-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              <p className="text-slate-600">Entretiens personnalisés avec chaque professionnelle</p>
            </div>
            <div className="flex items-center space-x-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              <p className="text-slate-600">Recommandations professionnelles requises</p>
            </div>
            <div className="flex items-center space-x-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              <p className="text-slate-600">Double vérification par les parents recommandée</p>
            </div>
          </div>
        </div>

        {/* Note Légale */}
        <div className="text-center text-slate-600 italic">
          <p>
            Les informations fournies sur cette page sont à titre informatif uniquement. 
            Pour plus de détails, veuillez consulter nos Conditions Générales d'Utilisation complètes.
          </p>
        </div>
      </div>

      {/* Styles pour les nuages */}
      <style jsx>{`
        .cloud {
          position: absolute;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 100px;
          animation: float 20s infinite linear;
        }

        .cloud-1 {
          width: 200px;
          height: 60px;
          top: 20%;
          left: -200px;
          animation-duration: 25s;
        }

        .cloud-2 {
          width: 150px;
          height: 45px;
          top: 40%;
          left: -150px;
          animation-duration: 30s;
          animation-delay: -10s;
        }

        .cloud-3 {
          width: 180px;
          height: 54px;
          top: 60%;
          left: -180px;
          animation-duration: 35s;
          animation-delay: -5s;
        }

        @keyframes float {
          from { transform: translateX(-100%); }
          to { transform: translateX(100vw); }
        }

        @media (max-width: 768px) {
          .cloud {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default LegalPage;